import * as d3 from 'd3';
import _ from 'lodash';

import {topoUtils} from './topo-utils';

import {QUARTILE_COLORS} from "../../../utilities/constants";

/**
 * The absolute center of some state polygons is in an
 * awkward position. This is a cheap approximation of
 * the center of mass offset from abs center
 */
const stateCentroidOffsets = {
  "12": [1.024, 1.02], //FL
  "26": [1.02, 1.16],  //MI
  "15": [1.05, 1.025],  //HI
  "34": [1.005, 1],    //NJ
  "24": [.995, .99],   //MD
  "50": [.999, .97],   //VT
  "33": [1, 1.03],     //NH
  "22": [0.98, 1],     //LA
  "16": [1, 1.1],      //ID
  "6": [0.9, 1.01]     //CA
};

class GeoUtils {

  /**
   * Create a geoAlbersUsa projection and geo path
   * with the map centered and scaled to fit.
   */
  getAlbersUsaPath(width, height) {
    const projection = d3.geoAlbersUsa()
      .translate([width / 2, height / 2.04])
      .scale(width * 1.22);

    return d3.geoPath().projection(projection);
  }

  /**
   * Returns a d3.geoPath.projection for the provided state fips
   * The projection is mercator for all states except Alaska
   *
   * @param stateFips
   * @param width
   * @param height
   * @returns {{path: *, projection: *}}
   */
  getAlbersStatePathProjection(stateFips, width, height) {
    const topo = topoUtils.getStateTopography(stateFips);
    const projection = stateFips === 2 ? d3.geoAlbersUsa() : d3.geoMercator();
    const path = d3.geoPath().projection(projection);

    projection.scale(1).translate([0, 0]);

    const b = path.bounds(topo.land),
      s = .95 / Math.max((b[1][0] - b[0][0]) / width, (b[1][1] - b[0][1]) / height),
      t = [(width - s * (b[1][0] + b[0][0])) / 2, (height - s * (b[1][1] + b[0][1])) / 2];

    projection.scale(s).translate(t);

    return {
      path,
      projection
    };
  }

  /**
   * Return getAlbersStatePathProjection.projection.path
   *
   * @param stateFips
   * @param width
   * @param height
   * @returns {*}
   */
  getAlbersStatePath(stateFips, width, height) {
    return this.getAlbersStatePathProjection(stateFips, width, height).path;
  }

  /**
   * Return getAlbersStatePathProjection.projection
   *
   * @param stateFips
   * @param width
   * @param height
   * @returns {*}
   */
  getAlbersStateProjection(stateFips, width, height) {
    return this.getAlbersStatePathProjection(stateFips, width, height).projection;
  }

  /**
   * Returns a slightly modified centroid based on the stateCentroidOffsets
   * multipliers. Used to ensure the centroid is in a visually appropriate place.
   *
   * @param stateFips
   * @param point
   * @returns {number[]|*}
   */
  offsetStateCentroid(stateFips, point) {
    const offset = stateCentroidOffsets[stateFips];

    if (offset) {
      return [
        point[0] * offset[0],
        point[1] * offset[1]
      ]
    }

    return point;
  }

  /**
   * Create an array of county path definitions using topography from
   * topoUtils.getStateTopography and getAlbersStatePath for state fips.
   *
   * The resulting json is enhanced with centroid properties
   * for display of hovers and labels
   *
   * @param stateFips
   * @param width
   * @param height
   * @returns {*|{}}
   */
  getMercatorStateCountyPaths(stateFips, width, height) {
    const topo = topoUtils.getStateTopography(stateFips);
    const path = this.getAlbersStatePath(stateFips, width, height);
    return _.map(topo.counties, d => {
      return {
        id: d.id,
        countyFips: d.id,
        stateFips: Math.floor(d.id / 1000),
        path: path(d),
        cent: path.centroid(d)
      }
    });
  }

  /**
   * Create an array of states path definitions using topography from
   * topoUtils.getNationalTopography and getAlbersUsaPath.
   *
   * The resulting json is enhanced with centroid properties
   * for display of hovers and labels
   *
   * @param width
   * @param height
   * @returns {*|{}}
   */
  getAlbersUsaStatePaths(width, height) {
    const topo = topoUtils.getNationalTopography();
    const path = this.getAlbersUsaPath(width, height);
    return _.map(topo.states, d => {
      return {
        id: d.id,
        stateFips: Math.floor(d.id / 1000),
        path: path(d),
        cent: this.offsetStateCentroid(d.id, path.centroid(d))
      }
    });
  }

  /**
   * A fill scale created specifically for our data types in this app.
   * This scale uses the value at 'dimension' expected to be a quartile (1-4)
   *
   * returns static colors for each quartile defined in
   * utilities/constants.QUARTILE_COLORS
   *
   * @param data
   * @param dimension
   * @returns {(function(): string)}
   */
  createQuartileFillScale(data, dimension) {
    const valueX = row => _.get(row, dimension);

    if (_.isEmpty(data)) {
      return () => '#FFFFFF'
    }

    const getFill = row => {
      switch (valueX(row)) {
        case 1:
          return QUARTILE_COLORS["1"];
        case 2:
          return QUARTILE_COLORS["2"];
        case 3:
          return QUARTILE_COLORS["3"];
        case 4:
          return QUARTILE_COLORS["4"];
        default:
          return "url(#crosshatch)";
      }
    }

    return getFill
  }

}

export let geoUtils = new GeoUtils();
